import React, { Component } from 'react';
import { Store } from 'lucide-react';
import Inherit from '../../../core/inherit';
import StoreController from './store.controller';
import withNavigate from '../../../core/withNavigate';

import { EmergencyInventory, InventoryTable, OutOfStock, Products, StoreTitle, StoreDetailAnalytics, StoreContractsSection, StoreCampaignsSection } from './elements';
import { Layout, CardSkeleton, AnalyticsSkeleton, RecentAudits, ShareOfShelf, MissingProducts, PageHeader } from '../../common';
import { PriceTable } from '../../storecheck/elements';

/**
 * Store (PDV) detail. Base page: a company overrides one section seam (`renderInventory`, …)
 * in a subclass registered as `pages.StoreProfile` in `companies/<id>/index.js`.
 */
export class ShelfStoreProfile extends Inherit(StoreController, Component) {
    state = {
        loading: true,
        store: {},
        storeChecks: [],
        elementsLoading: true,
        prices: [],
        contracts: [],
        campaigns: []
    };

    renderLoading() {
        return (
            <Layout>
                <div className="flex flex-col gap-y-6">
                    {/* Page header skeleton */}
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-surface-100 animate-pulse" />
                        <div>
                            <div className="w-48 h-6 rounded-md bg-surface-100 animate-pulse mb-2" />
                            <div className="w-64 h-4 rounded-md bg-surface-100 animate-pulse" />
                        </div>
                    </div>
                    <CardSkeleton rows={2} />
                    <AnalyticsSkeleton cards={4} />
                    <CardSkeleton rows={10} />
                </div>
            </Layout>
        );
    }

    renderHeader() {
        return <PageHeader icon={<Store />} title="Detalle de PDV" description="Información y auditorías del piso de venta" />;
    }

    /** Summary: identity, status, team and analysis period. */
    renderSummary() {
        const { store } = this.state;
        return <StoreTitle store={store} period={this.state.analysisRange} onPeriodChange={this.changeAnalysisPeriod.bind(this)} />;
    }

    /** KPIs + inventory alerts. */
    renderAnalytics() {
        const { store, storeChecks } = this.state;
        return <StoreDetailAnalytics store={store} storeChecks={storeChecks} />;
    }

    renderProducts() {
        return <Products store={this.state.store} />;
    }

    renderRecentAudits() {
        const { storeChecks, elementsLoading } = this.state;
        return <RecentAudits storeChecks={storeChecks} loading={elementsLoading} />;
    }

    renderMissingProducts(lastCheck) {
        return lastCheck?.products ? <MissingProducts products={lastCheck?.products ?? []} /> : null;
    }

    renderEmergencyInventory(lastCheck) {
        return lastCheck?.inventory ? <EmergencyInventory inventory={lastCheck?.inventory ?? []} /> : null;
    }

    renderOutOfStock(lastCheck) {
        return this.state.storeChecks.length > 0 ? <OutOfStock storeCheck={lastCheck ?? {}} /> : null;
    }

    /** Warehouse inventory of the last store check. */
    renderInventory(lastCheck) {
        return lastCheck?.inventory ? <InventoryTable inventory={lastCheck?.inventory ?? []} loading={this.state.elementsLoading} /> : null;
    }

    renderPriceTable() {
        const { prices, store } = this.state;
        return this.canSeePrices && prices && prices.length > 0 ? <PriceTable prices={prices} channel={store?.channel} /> : null;
    }

    renderShareOfShelf() {
        const { storeChecks } = this.state;
        return storeChecks.length > 0 ? <ShareOfShelf storeChecks={storeChecks} /> : null;
    }

    renderContracts() {
        return <StoreContractsSection contracts={this.state.contracts} />;
    }

    renderCampaigns() {
        return <StoreCampaignsSection campaigns={this.state.campaigns} />;
    }

    /** Collapsible sections, in order. */
    renderSections(lastCheck) {
        return (
            <div className="flex flex-col gap-y-4">
                {this.renderProducts()}
                {this.renderRecentAudits()}
                {this.renderMissingProducts(lastCheck)}
                {this.renderEmergencyInventory(lastCheck)}
                {this.renderOutOfStock(lastCheck)}
                {this.renderInventory(lastCheck)}
                {this.renderPriceTable()}
                {this.renderShareOfShelf()}
                {this.renderContracts()}
                {this.renderCampaigns()}
            </div>
        );
    }

    renderContent() {
        const lastCheck = this.lastStoreCheck();
        return (
            <Layout>
                {this.renderHeader()}

                <div className="flex flex-col gap-y-6">
                    {this.renderSummary()}
                    {this.renderAnalytics()}
                    {this.renderSections(lastCheck)}
                </div>
            </Layout>
        );
    }

    render() {
        if (this.state.loading) return this.renderLoading();
        return this.renderContent();
    }
}

export default withNavigate(ShelfStoreProfile);
